import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import useAsyncExport from 'hooks/useAsyncExport';
import { simpleDateAtTime } from 'lib/date';
import isDeepEqual from 'lib/isDeepEqual';
import { ClientType } from 'models/flexible/Client';
import type { User as UserType } from 'models/flexible/User';
import { PERMISSIONS } from 'models/Profile';
import type { AdminUserIndexFields } from 'services/api/fields';

const USER_INCLUDES = [
  'client',
  'credential',
  'allowedGroupNames',
  'role.permissions',
  'enabledProviderTypeNames',
] as const;

type UserRow = UserType<AdminUserIndexFields<(typeof USER_INCLUDES)[number]>>;
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexGroups } from 'services/api/admin/groups';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import { adminIndexRoles } from 'services/api/admin/roles';
import {
  adminCreateUserExport,
  adminDeleteUser,
  adminIndexUsers,
  adminReactivateUser,
  adminResendInvitation,
  adminUsersQueryKeys,
} from 'services/api/admin/users';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import DeactivateIcon from 'svg/DeactivateIcon';
import EditedPermissionsIcon from 'svg/EditedPermissionsIcon';
import EyeIcon from 'svg/EyeIcon';
import PencilIcon from 'svg/PencilIcon';
import ReactivateIcon from 'svg/ReactivateIcon';
import ResendInvitationIcon from 'svg/ResendInvitationIcon';

import UserGroupsSlideout from './UserGroupsSlideout';

// ---------------------------------------------------------------------------
// Users config
// ---------------------------------------------------------------------------

const usersConfig: AdminListPageConfig<UserRow> = {
  title: 'User Management',
  columnStorageKey: 'admin.users',
  queryKey: adminUsersQueryKeys.index(),
  fetchFn: (params, signal) => adminIndexUsers(params, signal, [...USER_INCLUDES]),
  includes: USER_INCLUDES,
  fetchParams: { defaultSort: 'userName asc', searchSort: 'search desc' },
  searchPlaceholder: 'Search Users',
  showResultCount: true,

  addButton: {
    label: 'Add User',
    path: '/admin/users/new',
    permission: 'adminUserCreate',
  },

  filters: [
    {
      key: 'client',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'users', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
    {
      key: 'clientType',
      label: 'Client Type',
      type: 'dropdown',
      staticOptions: Object.values(ClientType).map((v) => ({ id: v, name: v === ClientType.PAYOR ? 'Payer' : v })),
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'enabledProviderType',
      label: 'Enabled Care Options',
      type: 'multi_select',
      fetchConfig: {
        queryKey: ['adminGroupTypes', 'provider', 'users', 'filter'],
        fetchFn: indexGroupTypes,
        params: { type: 'provider', sortBy: 'displayName asc' },
      },
      getOptionLabel: (o) => o.displayName,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'group',
      label: 'Group',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminGroups', 'users', 'filter'],
        fetchFn: adminIndexGroups,
        params: { sortBy: 'name asc', include: 'groupType' },
      },
      getOptionLabel: (o) => o.name,
      formatOptionLabel: (o) => {
        const groupTypeName = o?.groupType?.displayName;
        return groupTypeName ? (
          <span>
            {o.name}
            <em>{` (${groupTypeName})`}</em>
          </span>
        ) : (
          (o?.name ?? '')
        );
      },
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
    {
      key: 'role',
      label: 'Role',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminRoles', 'users', 'filter'],
        fetchFn: adminIndexRoles,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
    {
      key: 'active',
      label: 'Include deactivated users',
      type: 'checkbox',
      toRequest: (value) => (value ? undefined : 'true'),
    },
  ],

  columns: [
    {
      id: 'userName',
      header: 'Name',
      render: (row) => {
        if (row.active) return <BoldText>{row.fullName}</BoldText>;
        return (
          <InactiveUserCell>
            <DeactivateIcon width={16} height={16} style={{ color: colors.black25, flexShrink: 0 }} />
            <InactiveUser>{row.fullName}</InactiveUser>
          </InactiveUserCell>
        );
      },
    },
    {
      id: 'email',
      header: 'Email',
      render: (row, ctx) => (
        <CopyableCell
          onClick={() => {
            navigator.clipboard.writeText(row.email);
            ctx.toast(`${row.email} copied to clipboard.`);
          }}>
          <TruncateCell>{row.email}</TruncateCell>
        </CopyableCell>
      ),
    },
    {
      id: 'roleName',
      header: 'Role',
      render: (row) => {
        const hasEditedPermissions = !isDeepEqual(row.permissions, row.role?.permissions);
        if (hasEditedPermissions) {
          return (
            <RoleCell>
              <EditedPermissionsIcon width={16} height={16} ovalFill={colors.yellow100} />
              <BodySmall>{row.role?.name}</BodySmall>
            </RoleCell>
          );
        }
        return <BodySmall>{row.role?.name}</BodySmall>;
      },
    },
    {
      id: 'clientName',
      header: 'Client',
      render: (row) => <BodySmall>{row.client?.name}</BodySmall>,
    },
    {
      id: 'clientType',
      header: 'Client Type',
      render: (row) => (row.client?.clientType === ClientType.PAYOR ? 'Payer' : (row.client?.clientType ?? '')),
    },
    {
      id: 'enabledProviderTypeNames',
      header: 'Enabled Care Options',
      sortable: false,
      render: (row) => row.enabledProviderTypeNames || '—',
    },
    {
      id: 'allowedGroupNames',
      header: 'Groups',
      sortable: false,
      render: (row, ctx) => {
        const groupNames = row.allowedGroupNames ?? [];
        if (groupNames.length === 0) return '—';
        if (groupNames.length === 1) return groupNames[0];
        return (
          <GroupsCountLink onClick={() => ctx.openSlideout('userGroups', row)}>{groupNames.length}</GroupsCountLink>
        );
      },
    },
    {
      id: 'lastLogin',
      header: 'Last Login',
      render: (row) => (row.lastLogin ? simpleDateAtTime(row.lastLogin) : ''),
    },
    {
      id: 'inviteSentAt',
      header: 'Invite Sent',
      render: (row) => (row.inviteSentAt ? simpleDateAtTime(row.inviteSentAt) : '—'),
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminUserView',
      visible: (row) => row.active,
      onClick: (row, { navigate }) => navigate(`/admin/users/${row.id}/edit`),
    },
    {
      label: 'View Preferences',
      icon: <EyeIcon width={18} height={18} />,
      permission: 'adminUserView',
      visible: (row) => row.active,
      onClick: (row, { navigate }) => navigate(`/admin/users/${row.id}/preferences`),
    },
    {
      label: 'Edit Permissions',
      icon: <EditedPermissionsIcon width={24} height={24} ovalFill='transparent' />,
      permission: 'adminUserView',
      visible: (row) => row.active,
      onClick: (row, { navigate }) => navigate(`/admin/users/${row.id}/permissions`),
    },
    {
      label: 'Send Invitation',
      icon: <ResendInvitationIcon width={18} height={18} />,
      permission: 'adminUserInvitationCreate',
      visible: (row) => row.active && !row.inviteSentAt,
      onClick: async (row, { toast }) => {
        try {
          await adminResendInvitation(row.id);
          toast('Invitation sent.');
        } catch {
          toast('Something went wrong. Please try again.');
        }
      },
    },
    {
      label: 'Resend Invitation',
      icon: <ResendInvitationIcon width={18} height={18} />,
      permission: 'adminUserInvitationCreate',
      visible: (row) => row.active && !!row.inviteSentAt,
      onClick: async (row, { toast }) => {
        try {
          await adminResendInvitation(row.id);
          toast('Invitation sent.');
        } catch {
          toast('Something went wrong. Please try again.');
        }
      },
    },
    {
      label: 'Deactivate',
      icon: <DeactivateIcon width={18} height={18} style={{ color: colors.red100 }} />,
      danger: true,
      permission: 'adminUserDelete',
      visible: (row) => row.active,
      onClick: (row, { openModal }) => openModal('deactivate', row),
    },
    {
      label: 'Reactivate User',
      icon: <ReactivateIcon width={18} height={18} />,
      permission: 'adminUserDelete',
      visible: (row) => !row.active,
      onClick: async (row, { refetch, toast }) => {
        try {
          await adminReactivateUser(row.id);
          toast('User successfully reactivated.');
          refetch();
        } catch {
          toast('Something went wrong. Please try again.');
        }
      },
    },
  ],

  modals: {
    deactivate: {
      actionText: 'deactivate',
      objType: 'user',
      subText: 'They can be reactivated at any time.',
      onConfirm: async (row, { toast }) => {
        try {
          await adminDeleteUser(row.id);
        } catch {
          toast('Something went wrong. Please try again.');
        }
      },
    },
  },

  slideouts: {
    userGroups: {
      render: (row, onClose) => (
        <UserGroupsSlideout groupNames={row.allowedGroupNames ?? []} userName={row.fullName} handleClose={onClose} />
      ),
    },
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Users() {
  const state = useAdminListPage(usersConfig);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { exporting, initiateExport } = useAsyncExport((params: Record<string, any>) => adminCreateUserExport(params));

  const configWithExport = useMemo(
    () => ({
      ...usersConfig,
      headerExtra: (
        <>
          {state.profile.has(PERMISSIONS.adminBulkImportCreate) && (
            <Button variant='outline-primary' onClick={() => navigate('/admin/bulk-import/new?type=users')}>
              Bulk import
            </Button>
          )}
          <Button loading={exporting} onClick={() => initiateExport(state.queryParams)}>
            Export User List
          </Button>
        </>
      ),
      modals: {
        ...usersConfig.modals,
        deactivate: {
          ...usersConfig.modals!.deactivate,
          onConfirm: async (row: UserRow, { toast }: { toast: (msg: string) => void }): Promise<void | false> => {
            try {
              await adminDeleteUser(row.id);
              queryClient.setQueriesData({ queryKey: ['adminUsers'] }, (old: any) => {
                if (!old?.data) return old;
                return {
                  ...old,
                  data: old.data.map((u: any) => {
                    if (u.id === row.id) {
                      u.active = false;
                    }
                    return u;
                  }),
                };
              });
            } catch {
              toast('Something went wrong. Please try again.');
            }
            return false;
          },
        },
      },
    }),
    [exporting, initiateExport, state.queryParams, state.profile, navigate, queryClient]
  );

  return <AdminListPageRenderer config={configWithExport} state={state} />;
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const InactiveUserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CopyableCell = styled.div`
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const TruncateCell = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InactiveUser = styled.span`
  font-weight: 500;
  color: ${colors.black25};
`;

const BoldText = styled.span`
  font-weight: 500;
`;

const RoleCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GroupsCountLink = styled.span`
  color: ${({ theme }) => theme.colors.blue100};
  cursor: pointer;
  text-decoration: underline;
`;
